import { z } from "zod";
import { SHARE_EFFECTS, SHARE_SCOPES } from "@/lib/enums";

/** `POST /api/shares`. No album and no photo is the whole project. */
export const createShareSchema = z
  .strictObject({
    projectId: z.string().min(1),
    audience: z.string().min(1).max(200),
    albumId: z.string().min(1).optional(),
    photoId: z.string().min(1).optional(),
    effect: z.enum(SHARE_EFFECTS),
    includeFuture: z.boolean().optional(),
  })
  .refine((body) => !(body.albumId && body.photoId), {
    message: "A share is on an album or a photo, not both",
  });
export type CreateShareRequest = z.infer<typeof createShareSchema>;

const MAX_IDS = 5000;

const shareFilters = {
  search: z.string().optional(),
  effect: z.enum(SHARE_EFFECTS).optional(),
  scope: z.enum(SHARE_SCOPES).optional(),
};

/** `POST /api/shares/revoke`: named shares, or everything a filter matches, like "Select all". */
export const revokeSharesSchema = z.union([
  z.strictObject({
    projectId: z.string().min(1),
    ids: z.array(z.string().min(1)).min(1).max(MAX_IDS),
  }),
  z.strictObject({
    projectId: z.string().min(1),
    all: z.literal(true),
    ...shareFilters,
    excludeIds: z.array(z.string().min(1)).max(MAX_IDS).optional(),
  }),
]);
export type RevokeSharesRequest = z.infer<typeof revokeSharesSchema>;

/** The query string of `GET /api/projects/:id/shares`. Unknown filters fall back to none. */
export const shareListQuerySchema = z.object({
  search: z.string().optional(),
  effect: z.enum(SHARE_EFFECTS).optional().catch(undefined),
  scope: z.enum(SHARE_SCOPES).optional().catch(undefined),
});

const shareRowSchema = z.object({
  id: z.string(),
  audience: z.string(),
  scope: z.enum(SHARE_SCOPES),
  /** The album, or "<file> in <album>"; null for the whole project. */
  scopeName: z.string().nullable(),
  effect: z.enum(SHARE_EFFECTS),
  includeFuture: z.boolean(),
  createdBy: z.string(),
  createdAt: z.string(),
  /** False when no IE account has this firm, so the share reaches nobody. */
  hasAccount: z.boolean(),
});
export type ShareRow = z.infer<typeof shareRowSchema>;

export const shareListResponseSchema = z.object({
  data: z.array(shareRowSchema),
  nextCursor: z.string().nullable(),
  prevCursor: z.string().nullable(),
  hasNext: z.boolean(),
  hasPrev: z.boolean(),
  total: z.number(),
});

const audienceSchema = z.object({ key: z.string(), label: z.string() });
export type ShareAudience = z.infer<typeof audienceSchema>;

export const shareSummaryResponseSchema = z.object({
  data: z.object({
    /** Every firm a PM can share with: the firms on IE accounts. */
    audiences: z.array(audienceSchema),
    /** Photos in each album an audience can see. Albums they can not see are not listed. */
    albums: z.array(
      z.object({ albumId: z.string(), audience: z.string(), sharedCount: z.number() }),
    ),
  }),
});
export type ShareSummary = z.infer<typeof shareSummaryResponseSchema>["data"];

export const photoShareStateResponseSchema = z.object({
  data: z.object({
    audiences: z.array(
      z.object({
        key: z.string(),
        label: z.string(),
        shared: z.boolean(),
        via: z.enum(SHARE_SCOPES).nullable(),
        reason: z.string(),
        /** The exclusion on this photo, to take back. */
        exclusionId: z.string().nullable(),
      }),
    ),
  }),
});
export type PhotoAudienceState = z.infer<
  typeof photoShareStateResponseSchema
>["data"]["audiences"][number];
